import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, Type, ViewChild, forwardRef } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCalendar, MatCalendarCellClassFunction } from '@angular/material/datepicker';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { UiDateRangeQuickHeaderComponent, UiDateRangeQuickHeaderService, UiDateRangeQuickOption } from '@netfoundry-ui/ui/date-range-quick-header';

type DateRangeValue = [Date | null, Date | null] | null;
type RangePart = 'start' | 'end';
type TimeState = { hour24: number; minute: number };

@Component({
    selector: 'app-date-time-range-picker',
    templateUrl: './date-time-range-picker.component.html',
    styleUrls: ['./date-time-range-picker.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UiDateTimeRangePickerComponent),
            multi: true,
        },
        UiDateRangeQuickHeaderService,
    ],
})
export class UiDateTimeRangePickerComponent implements ControlValueAccessor, OnChanges, OnDestroy {

    @ViewChild(MatCalendar) calendar!: MatCalendar<Date>;
    @Input() minDate: Date | null = null;
    @Input() maxDate: Date | null = null;
    @Input() placeholder = 'Select range';
    /** Display format for each endpoint */
    @Input() displayFormat = 'MM/DD/YY hh:mm A';
    /** 12 or 24 hour mode for the time spinner + formatting helpers */
    @Input() hourFormat: 12 | 24 = 12;
    @Input() ariaLabel = 'Open date & time range picker';
    /** CSS length, e.g. "28rem" or "520px" */
    @Input() panelWidth = '28rem';

    /** Optional: show calendar icon button in the input. */
    @Input() showIcon = true;

    /**
     * CDK overlay positions — preferred first. Falls back to a right-aligned
     * variant when the trigger is near the right viewport edge so the panel
     * doesn't spill off-screen.
     */
    overlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'end',   originY: 'bottom', overlayX: 'end',   overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
        { originX: 'end',   originY: 'top',    overlayX: 'end',   overlayY: 'bottom', offsetY: -4 },
    ];

    /**
     * Optional: override input display value (e.g. "Past Day").
     * When not provided, the input shows the formatted date-time range.
     */
    @Input() displayText = '';

    /**
     * Optional: quick-range buttons rendered in a custom calendar header.
     * When provided, a custom header is used and `quickRangeSelected` emits on click.
     */
    @Input() quickRanges: UiDateRangeQuickOption[] | null = null;
    /** Selected quick-range id for header selected-state */
    @Input() selectedQuickRange = '';
    /** Close overlay automatically after quick-range click */
    @Input() closeOnQuickRange = true;
    @Output() quickRangeSelected = new EventEmitter<string>();

    /** Show a PrimeNG-like action bar (Today/Clear/Close). */
    @Input() showButtonBar = false;
    @Input() showTodayButton = true;
    @Input() showClearButton = true;
    @Input() showCloseButton = true;
    @Input() todayLabel = 'Today';
    @Input() clearLabel = 'Clear';
    @Input() closeLabel = 'Close';

    open = false;
    disabled = false;

    start: Date | null = null;
    end: Date | null = null;

    // independent stored times for start/end (single UI spinner edits activePart)
    startTime: TimeState = { hour24: new Date().getHours(), minute: new Date().getMinutes() };
    endTime: TimeState = { hour24: new Date().getHours(), minute: new Date().getMinutes() };

    // which endpoint user is editing next
    activePart: RangePart = 'start';

    headerComponent: Type<unknown> | null = null;

    private onChange: (value: DateRangeValue) => void = () => {};
    private onTouched: () => void = () => {};
    private sub = new Subscription();

    constructor(private headerService: UiDateRangeQuickHeaderService) {
        this.sub.add(
            this.headerService.rangeClick$.subscribe((id) => {
                this.quickRangeSelected.emit(id);
                this.headerService.setSelectedRange(id);
                if (this.closeOnQuickRange) {
                    this.close();
                }
            })
        );
    }

    /** Adds range highlight classes (Material-like) in month view. */
    dateClass: MatCalendarCellClassFunction<Date> = (date, view) => {
        if (view !== 'month') {
            return '';
        }
        if (!date) {
            return '';
        }
        const start = this.start ? this.startOfDay(this.start) : null;
        const end = this.end ? this.startOfDay(this.end) : null;
        if (!start && !end) {
            return '';
        }

        const d = this.startOfDay(date);

        const isStart = !!start && d.getTime() === start.getTime();
        const isEnd = !!end && d.getTime() === end.getTime();

        if (isStart && isEnd) {
            return 'dtrp-range-start dtrp-range-end';
        }
        if (isStart) {
            return 'dtrp-range-start';
        }
        if (isEnd) {
            return 'dtrp-range-end';
        }
        if (start && end && d.getTime() > start.getTime() && d.getTime() < end.getTime()) {
            return 'dtrp-in-range';
        }
        return '';
    };

    get displayValue() {
        if (this.displayText) {
            return this.displayText;
        }
        const s = this.start ? moment(this.start).local().format(this.displayFormat) : '';
        const e = this.end ? moment(this.end).local().format(this.displayFormat) : '';
        if (!s && !e) {
            return '';
        }
        return `${s}${s || e ? ' - ' : ''}${e}`;
    }

    get startTimeText() {
        const fmt = this.hourFormat === 24 ? 'HH:mm' : 'hh:mm A';
        return this.start ? moment(this.start).local().format(fmt) : this.formatTimeFromState(this.startTime);
    }

    get endTimeText() {
        const fmt = this.hourFormat === 24 ? 'HH:mm' : 'hh:mm A';
        return this.end ? moment(this.end).local().format(fmt) : this.formatTimeFromState(this.endTime);
    }

    get activeHourDisplay() {
        const hour24 = this.getTime(this.activePart).hour24;
        if (this.hourFormat === 24) {
            return hour24;
        }
        const h = hour24 % 12;
        return h === 0 ? 12 : h;
    }

    get activeHour12() {
        const hour24 = this.getTime(this.activePart).hour24;
        const h = hour24 % 12;
        return h === 0 ? 12 : h;
    }

    get activeMinute() {
        return this.getTime(this.activePart).minute;
    }

    get activeMeridian() {
        return this.getTime(this.activePart).hour24 >= 12 ? 'PM' : 'AM';
    }

    writeValue(value: unknown): void {
        const v = (Array.isArray(value) ? value : null) as (Date | null)[] | null;
        const start = v?.[0] ? new Date(v[0]) : null;
        const end = v?.[1] ? new Date(v[1]) : null;
        this.start = start;
        this.end = end;
        this.syncTimesFromDates();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['quickRanges']) {
            if (this.quickRanges?.length) {
                this.headerComponent = UiDateRangeQuickHeaderComponent;
                this.headerService.setOptions(this.quickRanges);
            } else {
                this.headerComponent = null;
            }
        }
        if (changes['selectedQuickRange']) {
            this.headerService.setSelectedRange(this.selectedQuickRange || '');
        }
        // if quickRanges are set but headerComponent wasn't yet set (first change cycle)
        if (!this.headerComponent && this.quickRanges?.length) {
            this.headerComponent = UiDateRangeQuickHeaderComponent;
            this.headerService.setOptions(this.quickRanges);
        }
    }

    registerOnChange(fn: (value: DateRangeValue) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (this.disabled) {
            this.open = false;
        }
    }

    toggle() {
        if (this.disabled) {
            return;
        }
        this.open = !this.open;
        if (this.open) {
            this.syncTimesFromDates();
        } else {
            this.onTouched();
        }
    }

    close() {
        this.open = false;
        this.onTouched();
    }

    setToday() {
        if (this.disabled) {
            return;
        }
        const now = new Date();
        const start = new Date(now);
        const end = new Date(now);
        start.setHours(this.startTime.hour24, this.startTime.minute, start.getSeconds() + 5, start.getMilliseconds());
        end.setHours(this.endTime.hour24, this.endTime.minute, end.getSeconds() + 5, end.getMilliseconds());
        this.start = start;
        this.end = end;
        this.activePart = 'end';
        this.emitChange();
    }

    clear() {
        if (this.disabled) {
            return;
        }
        this.start = null;
        this.end = null;
        this.activePart = 'start';
        this.onChange(null);
    }

onDateSelected(newDate: Date | null) {
    if (!newDate) return;
    const selectedDate = new Date(newDate);

    // 1. If we are starting fresh OR if we already have a complete range
    if (!this.start || (this.start && this.end)) {
        selectedDate.setHours(this.startTime.hour24, this.startTime.minute, 0, 0);
        this.start = selectedDate;
        this.end = null;      // Reset end so we are in "selection mode"
        this.activePart = 'end'; // Tell the calendar to focus on the next slot
    } 
    // 2. If we are picking the second date
    else {
        selectedDate.setHours(this.endTime.hour24, this.endTime.minute, 0, 0);
        
        if (selectedDate < this.start) {
            // Swap them if the user picked an earlier date
            this.end = new Date(this.start);
            this.start = selectedDate;
        } else {
            this.end = selectedDate;
        }
        
        // After finishing a range, we stay on 'end' or flip to 'start' 
        // depending on which one you want the Material "focus ring" to highlight.
        this.activePart = 'end'; 
    }

    this.emitChange();

    // 3. FORCE REFRESH: This is the "magic" that makes the dots update
    if (this.calendar) {
        this.calendar.updateTodaysDate();
    }
}
    setActive(part: RangePart) {
        this.activePart = part;
        // if date exists, ensure the time spinner matches it (e.g. external writes)
        this.syncTimesFromDates();
    }

    adjustHour(delta: number) {
        this.setTime(this.activePart, (t) => ({ ...t, hour24: (t.hour24 + delta + 24) % 24 }));
        this.applyTimeToDate(this.activePart);
    }

    adjustMinute(delta: number) {
        this.setTime(this.activePart, (t) => ({ ...t, minute: (t.minute + delta + 60) % 60 }));
        this.applyTimeToDate(this.activePart);
    }

    toggleMeridian() {
        if (this.hourFormat === 24) {
            return;
        }
        this.setTime(this.activePart, (t) => ({ ...t, hour24: (t.hour24 + 12) % 24 }));
        this.applyTimeToDate(this.activePart);
    }

    twoDigit(n: number) {
        return String(Number.isFinite(n) ? n : 0).padStart(2, '0');
    }

    private syncTimesFromDates() {
        if (this.start) {
            const d = new Date(this.start);
            this.startTime = { hour24: d.getHours(), minute: d.getMinutes() };
        }
        if (this.end) {
            const d = new Date(this.end);
            this.endTime = { hour24: d.getHours(), minute: d.getMinutes() };
        }
    }

    private applyTimeToDate(part: RangePart) {
        if (part === 'start') {
            if (!this.start) {
                return;
            }
            const d = new Date(this.start);
            d.setHours(this.startTime.hour24, this.startTime.minute, d.getSeconds() + 5, d.getMilliseconds());
            this.start = d;
            this.emitChange();
            return;
        }

        if (!this.end) {
            return;
        }
        const d = new Date(this.end);
        d.setHours(this.endTime.hour24, this.endTime.minute, d.getSeconds() + 5, d.getMilliseconds());
        this.end = d;
        this.emitChange();
    }

    private emitChange() {
        this.onChange([this.start, this.end]);
    }

    private startOfDay(date: Date) {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    private getTime(part: RangePart): TimeState {
        return part === 'start' ? this.startTime : this.endTime;
    }

    private setTime(part: RangePart, updater: (t: TimeState) => TimeState) {
        const next = updater(this.getTime(part));
        const normalized: TimeState = {
            hour24: (next.hour24 + 24) % 24,
            minute: (next.minute + 60) % 60,
        };
        if (part === 'start') {
            this.startTime = normalized;
        } else {
            this.endTime = normalized;
        }
    }

    private formatTimeFromState(t: TimeState) {
        const fmt = this.hourFormat === 24 ? 'HH:mm' : 'hh:mm A';
        return moment().startOf('day').hour(t.hour24).minute(t.minute).format(fmt);
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }
}

